import { Link, useNavigate } from "react-router-dom";
import { Bell, Compass, LogOut, MessageSquare, Store, User } from "lucide-react";
import { CountBadge } from "@/components/CountBadge";
import { useUnread } from "@/lib/useUnread";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { MobileMenu } from "@/components/MobileMenu";
import { Avatar } from "@/components/Avatar";
import { BottomTabs } from "@/components/BottomTabs";

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const unread = useUnread(!!user);
  const isBiz = user && (user.role === "vendor" || user.role === "admin");

  return (
    <>
    {user && <BottomTabs user={user} unread={unread} />}
    <header className="sticky top-0 z-50 bg-[#0B0F19]/85 backdrop-blur-xl border-b border-[#26334D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="h-9 w-9 rounded-xl bg-[#FF3B6B] grid place-items-center transition-transform duration-300 group-hover:rotate-12">
            <Compass className="h-5 w-5" />
          </span>
          <span className="font-extrabold text-lg tracking-tight">Vaca<span className="text-[#FF3B6B]">Vibe</span></span>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/pricing" data-testid="nav-pricing" className="hidden sm:inline px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors">Pricing</Link>
          {user ? (
            <>
              <div className="hidden md:flex items-center gap-1">
                <Link to="/explore" data-testid="nav-explore" className="px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors">Explore</Link>
                <Link to="/trip" data-testid="nav-trip" className="px-3 py-2 text-sm text-[#00E5A3] hover:text-white transition-colors">My Trip</Link>
                <Link to="/following" data-testid="nav-following" className="px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors">Following</Link>
                <Link to="/traveler/me" data-testid="nav-profile" className="px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors">Profile</Link>
                {isBiz && (
                  <Link to="/vendor" data-testid="vendor-dashboard-nav" className="px-3 py-2 text-sm text-[#FFB800] hover:text-white transition-colors flex items-center gap-1"><Store className="h-4 w-4" /> My Business</Link>
                )}
                {isBiz && (
                  <Link to="/vendor/reviews" data-testid="nav-reviews" className="px-3 py-2 text-sm text-[#FFB800] hover:text-white transition-colors flex items-center gap-1"><MessageSquare className="h-4 w-4" /> Reviews <CountBadge n={unread.business} testid="nav-reviews-badge" /></Link>
                )}
                <Link to="/notifications" data-testid="nav-notifications" aria-label="Notifications" className="relative px-2 py-2 text-slate-300 hover:text-white transition-colors">
                  <Bell className="h-4 w-4" />
                  <span className="absolute -top-0.5 -right-1"><CountBadge n={unread.personal} testid="nav-notifications-badge" /></span>
                </Link>
                {user.role === "admin" && <Link to="/admin" data-testid="nav-owner" className="px-3 py-2 text-sm text-[#FF3B6B] hover:text-white transition-colors">Owner</Link>}
                <Link to="/account" data-testid="nav-account" className="px-3 py-2 text-sm text-slate-300 hover:text-white flex items-center gap-1">
                  {user.avatar_url ? <Avatar url={user.avatar_url} name={user.name} size="h-7 w-7" testid="nav-avatar" /> : <User className="h-4 w-4" />} <span className="hidden lg:inline">{user.name.split(" ")[0]}</span>
                </Link>
                <Button data-testid="nav-logout" variant="ghost" size="icon" className="rounded-full"
                  onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
              <MobileMenu user={user} unread={unread} onLogout={async () => { await logout(); navigate("/"); }} />
            </>
          ) : user === false ? (
            <>
              <Link to="/login" data-testid="nav-login" className="px-3 py-2 text-sm text-slate-300 hover:text-white">Log in</Link>
              <Button data-testid="nav-signup" onClick={() => navigate("/register")}
                className="rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">
                Start free trial
              </Button>
            </>
          ) : null}
        </nav>
      </div>
    </header>
    </>
  );
}
